import apiClient from './api'

/**
 * Servicio del Módulo de Soporte.
 * - customer*: usuario de una empresa (solo ve sus propios casos)
 * - admin*: bandeja de atención (administradores del sistema y agentes de soporte)
 */

const ok = (response) => ({ success: true, data: response.data.data, message: response.data.message })
const fail = (error, fallback) => ({
  success: false,
  status: error.response?.status,
  message: error.response?.data?.message || fallback,
  errors: error.response?.data?.errors || null
})

const toFormData = (payload = {}, files = []) => {
  const fd = new FormData()
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    fd.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : value)
  })
  files.forEach((file) => fd.append('attachments[]', file))
  return fd
}

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } }

// Evento global para que el menú actualice sus contadores apenas cambie el estado de lectura
export const SUPPORT_UNREAD_EVENT = 'zentry:support-unread-changed'
export const notifySupportUnreadChanged = () => {
  window.dispatchEvent(new CustomEvent(SUPPORT_UNREAD_EVENT))
}

let cachedVersion = null
export const getAppVersion = async () => {
  if (cachedVersion !== null) return cachedVersion
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`)
    const data = await res.json()
    cachedVersion = data?.version || ''
  } catch {
    cachedVersion = ''
  }
  return cachedVersion
}

export const supportService = {
  // ---------------- Cliente ----------------
  async customerMeta(companyId) {
    try { return ok(await apiClient.get(`/companies/${companyId}/support/meta`)) }
    catch (e) { return fail(e, 'Error al cargar catálogos de soporte') }
  },

  async customerTickets(companyId, params = {}) {
    try { return ok(await apiClient.get(`/companies/${companyId}/support/tickets`, { params })) }
    catch (e) { return fail(e, 'Error al cargar tus casos') }
  },

  async customerCreate(companyId, payload, files = []) {
    try { return ok(await apiClient.post(`/companies/${companyId}/support/tickets`, toFormData(payload, files), multipart)) }
    catch (e) { return fail(e, 'No se pudo registrar el caso') }
  },

  async customerShow(companyId, ticketId) {
    try { return ok(await apiClient.get(`/companies/${companyId}/support/tickets/${ticketId}`)) }
    catch (e) { return fail(e, 'No se pudo abrir el caso') }
  },

  async customerPoll(companyId, ticketId, afterId) {
    try { return ok(await apiClient.get(`/companies/${companyId}/support/tickets/${ticketId}/messages`, { params: { after_id: afterId } })) }
    catch (e) { return fail(e, 'Error al actualizar la conversación') }
  },

  async customerReply(companyId, ticketId, body, files = []) {
    try { return ok(await apiClient.post(`/companies/${companyId}/support/tickets/${ticketId}/messages`, toFormData({ body }, files), multipart)) }
    catch (e) { return fail(e, 'No se pudo enviar el mensaje') }
  },

  async customerUnreadCount(companyId) {
    try { return ok(await apiClient.get(`/companies/${companyId}/support/unread-count`)) }
    catch (e) { return fail(e, '') }
  },

  // ---------------- Agente ----------------
  async adminMeta() {
    try { return ok(await apiClient.get('/admin/support/meta')) }
    catch (e) { return fail(e, 'Error al cargar catálogos de soporte') }
  },

  async adminTickets(params = {}) {
    try { return ok(await apiClient.get('/admin/support/tickets', { params })) }
    catch (e) { return fail(e, 'Error al cargar la bandeja') }
  },

  async adminShow(ticketId) {
    try { return ok(await apiClient.get(`/admin/support/tickets/${ticketId}`)) }
    catch (e) { return fail(e, 'No se pudo abrir el caso') }
  },

  async adminPoll(ticketId, afterId) {
    try { return ok(await apiClient.get(`/admin/support/tickets/${ticketId}/messages`, { params: { after_id: afterId } })) }
    catch (e) { return fail(e, 'Error al actualizar la conversación') }
  },

  async adminReply(ticketId, body, isInternal = false, files = []) {
    try { return ok(await apiClient.post(`/admin/support/tickets/${ticketId}/messages`, toFormData({ body, is_internal: isInternal }, files), multipart)) }
    catch (e) { return fail(e, 'No se pudo enviar el mensaje') }
  },

  async adminUpdate(ticketId, payload) {
    try { return ok(await apiClient.patch(`/admin/support/tickets/${ticketId}`, payload)) }
    catch (e) { return fail(e, 'No se pudo actualizar el caso') }
  },

  async adminUnreadCount() {
    try { return ok(await apiClient.get('/admin/support/unread-count')) }
    catch (e) { return fail(e, '') }
  },

  // ---------------- Administración (solo administradores del sistema) ----------------
  async adminStats(month = null) {
    try { return ok(await apiClient.get('/admin/support/stats', { params: month ? { month } : {} })) }
    catch (e) { return fail(e, 'Error al cargar los indicadores') }
  },

  async adminCategories() {
    try { return ok(await apiClient.get('/admin/support/categories')) }
    catch (e) { return fail(e, 'Error al cargar las categorías') }
  },

  async adminCreateCategory(payload) {
    try { return ok(await apiClient.post('/admin/support/categories', payload)) }
    catch (e) { return fail(e, 'No se pudo crear la categoría') }
  },

  async adminUpdateCategory(id, payload) {
    try { return ok(await apiClient.put(`/admin/support/categories/${id}`, payload)) }
    catch (e) { return fail(e, 'No se pudo actualizar la categoría') }
  },

  async adminDeleteCategory(id) {
    try { return ok(await apiClient.delete(`/admin/support/categories/${id}`)) }
    catch (e) { return fail(e, 'No se pudo eliminar la categoría') }
  },

  async adminReorderCategories(ids) {
    try { return ok(await apiClient.post('/admin/support/categories/reorder', { ids })) }
    catch (e) { return fail(e, 'No se pudo guardar el orden') }
  },

  async adminSettings() {
    try { return ok(await apiClient.get('/admin/support/settings')) }
    catch (e) { return fail(e, 'Error al cargar la configuración') }
  },

  async adminUpdateSettings(payload) {
    try { return ok(await apiClient.put('/admin/support/settings', payload)) }
    catch (e) { return fail(e, 'No se pudo guardar la configuración') }
  },

  // ---------------- Adjuntos ----------------
  /** Descarga un adjunto con el token de sesión y devuelve un Blob. */
  async fetchAttachment(relativeUrl) {
    const response = await apiClient.get(`/${relativeUrl.replace(/^\/+/, '')}`, { responseType: 'blob' })
    return response.data
  }
}

export default supportService
